/**
 * Two languages, one catalogue shape: German is the reference (`de.ts`) and
 * English is typed against it, so a missing key never reaches the browser.
 *
 * The FORM stays German — the PDF is the LBA original and the abbreviations
 * (rwK, mwSK, WCA, VAR, GS, ETO/ATO, TL …) are what is printed on the paper.
 * Only the app's own prose changes language.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { de } from './de';
import { en } from './en';

export type Lang = 'de' | 'en';
export type Vars = Record<string, string | number>;
/** The translate function every component gets from {@link useT}. */
export type T = (key: string, vars?: Vars) => string;

const DICTS: Record<Lang, Readonly<Record<string, string>>> = { de, en };

export const LANG_KEY = 'avia.lang';

/** Does the catalogue know this key? (`de` holds every key by construction.) */
export const hasKey = (key: string): boolean => Object.prototype.hasOwnProperty.call(de, key);

/** `t` without a React context — for the few places that run outside render. */
export function translate(lang: Lang, key: string, vars?: Vars): string {
  const raw = DICTS[lang][key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (m, name: string) => {
    const v = vars[name];
    return v === undefined ? m : String(v);
  });
}

/* ---------------- warnings ---------------- */

/** A warning as the web app carries it: a stable code plus its substitutions. */
export interface WarnLike {
  code: string;
  params?: Record<string, string> | undefined;
  /** @avia/core's German message — used only when the code is not in the catalogue */
  fallback?: string | undefined;
}

/**
 * Warning text for a code. Unknown codes fall back to core's own message, so a
 * check added in @avia/core still says something useful before it is translated.
 */
export function warnText(t: T, w: WarnLike): string {
  const key = 'warn.' + w.code;
  if (!hasKey(key)) return w.fallback ?? key;
  const p = w.params;
  const leg = p?.['leg'];
  if (p && leg !== undefined) return t(key, { ...p, leg: leg === 'alt' ? t('form.alt') : leg });
  return t(key, p);
}

/* ---------------- provider ---------------- */

function initialLang(): Lang {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored === 'de' || stored === 'en') return stored;
  } catch {
    /* private mode: fall through to the browser's preference */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language : '';
  return nav.toLowerCase().startsWith('de') ? 'de' : 'en';
}

interface LangValue {
  lang: Lang;
  setLang(l: Lang): void;
}

const LangContext = createContext<LangValue | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = translate(lang, 'app.title');
    document.querySelector('meta[name=description]')?.setAttribute('content', translate(lang, 'app.description'));
  }, [lang]);

  const value = useMemo<LangValue>(
    () => ({
      lang,
      setLang(l) {
        setLangState(l);
        try {
          localStorage.setItem(LANG_KEY, l);
        } catch {
          /* the choice simply does not survive the tab */
        }
      },
    }),
    [lang],
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang(): LangValue {
  const v = useContext(LangContext);
  if (!v) throw new Error('useLang outside a <LangProvider>');
  return v;
}

/** The translate function, stable per language. */
export function useT(): T {
  const { lang } = useLang();
  return useCallback<T>((key, vars) => translate(lang, key, vars), [lang]);
}
